import type { JunipProduct } from 'types/reviews';
import type { JunipProductWithReviews } from 'types/reviews/JunipProduct';
import type { JunipRatingDistribution } from 'types/reviews/JunipRatingDistribution';

import { getRatingAverage } from './getRatingAverage';

export interface AggregatedProductRatings {
  ratingAverage: number;
  ratingCount: number;
  recommendedPercentage?: number;
  ratingDistribution?: JunipRatingDistribution;
}

const hasReviews = (
  product: JunipProduct,
): product is JunipProductWithReviews =>
  product.ratingAverage != null && product.ratingCount != null;

export const aggregateProductRatings = (
  products: JunipProduct[],
): AggregatedProductRatings => {
  const withReviews = products.filter(hasReviews);

  if (!withReviews.length) {
    return { ratingAverage: 0, ratingCount: 0 };
  }

  const { average, count } = getRatingAverage(
    withReviews.map((product) => ({
      average: product.ratingAverage,
      count: product.ratingCount,
    })),
  );

  let recommendedWeighted = 0;
  let recommendedTotal = 0;
  let hasDistribution = false;
  const ratingDistribution: JunipRatingDistribution = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  };

  withReviews.forEach((product) => {
    if (product.recommendedPercentage != null) {
      recommendedWeighted +=
        product.recommendedPercentage * product.ratingCount;
      recommendedTotal += product.ratingCount;
    }

    const distribution = product.ratingDistribution;
    if (!distribution) return;

    hasDistribution = true;
    ratingDistribution[1] += distribution[1];
    ratingDistribution[2] += distribution[2];
    ratingDistribution[3] += distribution[3];
    ratingDistribution[4] += distribution[4];
    ratingDistribution[5] += distribution[5];
  });

  const recommendedPercentage =
    recommendedTotal > 0 ? recommendedWeighted / recommendedTotal : undefined;

  return {
    ratingAverage: average,
    ratingCount: count,
    recommendedPercentage,
    ratingDistribution: hasDistribution ? ratingDistribution : undefined,
  };
};
